import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Info } from "lucide-react";

// Import images
import img0 from "@/assets/img/ahj-tour/ahj-tour-0.png";
import img1 from "@/assets/img/ahj-tour/ahj-tour-1.png";
import img2 from "@/assets/img/ahj-tour/ahj-tour-2.png";
import img3 from "@/assets/img/ahj-tour/ahj-tour-3.png";

interface Step {
  image: string;
  title: string;
  description: React.ReactNode;
}

const STEPS: Step[] = [
  {
    image: img0,
    title: "AHJ Details Overview",
    description: (
      <span>
        View comprehensive Authority Having Jurisdiction (AHJ) details including
        contact information and location by clicking the info icon{" "}
        <Info className="inline-block h-4 w-4 text-primary align-text-bottom" />{" "}
        beside AHJ on the project information card.
      </span>
    ),
  },
  {
    image: img1,
    title: "Permitting Requirements",
    description:
      "Access detailed AHJ-specific permitting requirements, submittal processes, and review timelines.",
  },
  {
    image: img2,
    title: "Required Documents",
    description:
      "When you create a project with an AHJ, you can now see a new step to upload all required documents for the selected AHJ.",
  },
  {
    image: img3,
    title: "Missing Document Alerts",
    description:
      "Easily identify missing AHJ-required documents with new alerts. Additionally, all AHJ-specific documents can now be viewed and managed directly from the main Documents tab.",
  },
];

interface AHJFeatureWalkthroughModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AHJFeatureWalkthroughModal: React.FC<
  AHJFeatureWalkthroughModalProps
> = ({ open, onOpenChange }) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Reset step when modal opens
  useEffect(() => {
    if (open) {
      setCurrentStep(0);
    }
  }, [open]);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    localStorage.setItem("hasSeenAHJWalkthrough_v1", "true");
    onOpenChange(false);
  };

  const step = STEPS[currentStep];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden gap-0 border-border/50 shadow-sophisticated">
        <div className="relative aspect-video w-full bg-muted/20 border-b border-border/50">
          <img
            src={step.image}
            alt={step.title}
            className="w-full h-full object-contain"
          />
        </div>
        
        <div className="p-6 space-y-4">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">{step.title}</DialogTitle>
          </DialogHeader>
          
          <p className="text-muted-foreground text-sm leading-relaxed min-h-[60px]">
            {step.description}
          </p>

          <div className="flex items-center justify-between pt-4">
            {/* Step Indicators */}
            <div className="flex gap-1.5">
              {STEPS.map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    "h-2 w-2 rounded-full transition-all duration-300",
                    index === currentStep
                      ? "bg-primary w-6"
                      : "bg-muted-foreground/30"
                  )}
                />
              ))}
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 0}
                className="hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                Back
              </Button>
              <Button 
                onClick={handleNext}
                className="btn-gradient shadow-md hover:shadow-lg transition-all"
              >
                {currentStep === STEPS.length - 1 ? "Finish" : "Next"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
